<template>
  <footer class="footer">
    <div class="container footer-container">
      <div class="footer-logo">
        <img :src="siteConfig.footerLogo || 'img/footer-logo.png'" class="footer-logo-img" alt="吕氏家谱网">
        <img src="img/footer-jt.png" class="footer-icon" alt="吕氏家谱网">
      </div>
      <div class="footer-con">
        <div class="footer-links">
          <div class="footer-column" v-for="(column, index) in footerColumns" :key="`${column.title}-${index}`">
            <h3>{{ column.title }}</h3>
            <ul>
              <li v-for="(link, linkIndex) in column.links" :key="`${link.name}-${linkIndex}`">
                <router-link v-if="isRouteLink(link.url)" :to="link.url">{{ link.name }}</router-link>
                <a
                  v-else
                  :href="link.url || '#'"
                  :target="link.target || '_self'"
                  :rel="link.target === '_blank' ? 'noreferrer' : null"
                >
                  {{ link.name }}
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div class="footer-contact">
          <div class="contact-info">
            <h3>全国咨询电话</h3>
            <p class="phone">{{ contact.phone }}</p>
            <p>咨询邮箱：{{ contact.consultEmail }}</p>
            <p>联系邮箱：{{ contact.email }}</p>
            <p>联系地址：{{ contact.address }}</p>
          </div>
        </div>
      </div>
    </div>
    <div class="footer-bottom">
      <div class="container">
        <p>
          <span>版权所有：</span>
          <span>{{ footerCopyrightOwner }}</span>
          <a v-if="hasIcpUrl" :href="siteConfig.icpUrl" target="_blank" rel="noreferrer">{{ footerIcp }}</a>
          <span v-else-if="footerIcp">{{ footerIcp }}</span>
          <span v-if="footerMemberNo">会员登记号：{{ footerMemberNo }}</span>
        </p>
        <p>
          <span>技术支持：</span>
          <a v-if="siteConfig.techSupportUrl" :href="siteConfig.techSupportUrl" target="_blank" rel="noreferrer">{{ footerTechSupport }}</a>
          <span v-else>{{ footerTechSupport }}</span>
        </p>
      </div>
    </div>
  </footer>
</template>

<script>
import { getSiteConfig, getFooterConfig } from '@/api/config'

const DEFAULT_SITE_CONFIG = {
  siteName: '吕氏家谱网',
  footerLogo: 'img/footer-logo.png',
  copyright: '吕氏家谱网',
  icp: '鲁ICP备12345678-1',
  icpUrl: '',
  memberNo: '1234567890765',
  techSupport: '吕氏科技',
  techSupportUrl: '',
  contact: {
    phone: '40530-3338333',
    consultEmail: '(0800) 6666-0000',
    email: 'lvs@lvs.com',
    address: '山东省菏泽市单县中心220*号'
  }
}

const DEFAULT_FOOTER_COLUMNS = [
  {
    title: '吕氏家谱',
    links: [
      { name: '谱系结构', url: '/genealogy/analysis' },
      { name: '新增成员', url: '/register' },
      { name: '信息修改', url: '/login' },
      { name: '成员动态', url: '/news' }
    ]
  },
  {
    title: '谱系设计',
    links: [
      { name: '在线管理', url: '/login' },
      { name: '吕氏文化', url: '/culture' }
    ]
  },
  {
    title: '历史趋势',
    links: [
      { name: '先秦时期', url: '#' },
      { name: '秦汉时期', url: '#' },
      { name: '唐宋元明清', url: '#' },
      { name: '近现代', url: '#' }
    ]
  },
  {
    title: '谱系分布',
    links: [
      { name: '华东地区', url: '/distribution' },
      { name: '华南地区', url: '/distribution' },
      { name: '华北地区', url: '/distribution' },
      { name: '西北地区', url: '/distribution' }
    ]
  },
  {
    title: '宗亲互动',
    links: [
      { name: '在线寻亲', url: '/interaction' },
      { name: '宗亲留言', url: '/clan/guestbook' },
      { name: '宗亲活动', url: '/clan/activities' },
      { name: '宗亲论坛', url: '/interaction/forum' }
    ]
  },
  {
    title: '吕氏祠堂',
    links: [
      { name: '吕氏宗祠', url: '/citang?type=ancestral' },
      { name: '吕氏支祠', url: '/citang?type=branch' },
      { name: '吕氏家祠', url: '/citang?type=family' },
      { name: '更多祠堂', url: '/citang' }
    ]
  },
  {
    title: '吕氏名人',
    links: [
      { name: '古代名人', url: '/celebrities/ancient' },
      { name: '近现代名人', url: '/celebrities/modern' },
      { name: '名人典故', url: '/celebrities/stories' },
      { name: '名人总览', url: '/celebrities' }
    ]
  }
]

const cloneFooterColumns = () => DEFAULT_FOOTER_COLUMNS.map(column => ({
  ...column,
  links: column.links.map(link => ({ ...link }))
}))

export default {
  name: 'AppFooter',
  data() {
    return {
      siteConfig: {
        ...DEFAULT_SITE_CONFIG,
        contact: { ...DEFAULT_SITE_CONFIG.contact }
      },
      footerColumns: cloneFooterColumns(),
      contact: { ...DEFAULT_SITE_CONFIG.contact }
    }
  },
  computed: {
    footerCopyrightOwner() {
      const rawText = String(this.siteConfig.copyrightOwner || this.siteConfig.copyright || this.siteConfig.siteName || '')
        .replace(/^版权所有[:：]?\s*/, '')
        .replace(/^©\s*\d{4}\s*/, '')
        .replace(/版权所有$/, '')
        .trim()

      return rawText || DEFAULT_SITE_CONFIG.siteName
    },
    footerIcp() {
      return this.siteConfig.icp || ''
    },
    footerMemberNo() {
      return this.siteConfig.memberNo || this.siteConfig.memberCode || this.siteConfig.memberRegistrationNo || ''
    },
    footerTechSupport() {
      return this.siteConfig.techSupport || DEFAULT_SITE_CONFIG.techSupport
    },
    hasIcpUrl() {
      return Boolean(this.siteConfig.icpUrl && this.siteConfig.icpUrl !== '#')
    }
  },
  created() {
    this.fetchConfig()
  },
  methods: {
    async fetchConfig() {
      const [siteResult, footerResult] = await Promise.allSettled([
        getSiteConfig(),
        getFooterConfig()
      ])

      if (siteResult.status === 'fulfilled') {
        this.applySiteConfig(siteResult.value)
      } else {
        console.error('获取站点配置失败:', siteResult.reason)
        this.applySiteConfig(DEFAULT_SITE_CONFIG)
      }

      if (footerResult.status === 'fulfilled') {
        this.applyFooterColumns(footerResult.value && footerResult.value.columns)
      } else {
        console.warn('底部配置接口不可用，使用默认配置:', footerResult.reason)
        this.applyFooterColumns(DEFAULT_FOOTER_COLUMNS)
      }
    },
    applySiteConfig(siteConfig = {}) {
      this.siteConfig = this.normalizeSiteConfig(siteConfig)
      this.contact = this.siteConfig.contact
    },
    applyFooterColumns(columns = []) {
      this.footerColumns = this.normalizeFooterColumns(columns)
    },
    normalizeSiteConfig(siteConfig = {}) {
      return {
        ...DEFAULT_SITE_CONFIG,
        ...siteConfig,
        contact: {
          ...DEFAULT_SITE_CONFIG.contact,
          ...(siteConfig.contact || {})
        }
      }
    },
    normalizeFooterColumns(columns = []) {
      const sourceColumns = Array.isArray(columns) ? columns : []

      return cloneFooterColumns().map((defaultColumn, columnIndex) => {
        const currentColumn = sourceColumns[columnIndex] || {}
        const currentLinks = Array.isArray(currentColumn.links) ? currentColumn.links : []

        return {
          title: currentColumn.title || defaultColumn.title,
          links: defaultColumn.links.map((defaultLink, linkIndex) => ({
            ...defaultLink,
            ...(currentLinks[linkIndex] || {})
          }))
        }
      })
    },
    isRouteLink(url) {
      return typeof url === 'string' && /^\//.test(url)
    }
  }
}
</script>

<style scoped>
.footer {
  background-color: #1a1a1a;
  color: #fff;
  padding: 40px 0 20px;
}

.footer-container {
  padding: 0;
}

.footer-logo {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  border-bottom: 2px solid #5e6065;
  padding-bottom: 20px;
}

.footer-logo-img {
  width: 238px;
  height: auto;
  margin-right: 15px;
}

.footer-icon {
  width: 24px;
  height: auto;
}

.footer-con {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 40px;
}

.footer-links {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 24px 20px;
  margin-bottom: 30px;
}

.footer-column {
  min-width: 0;
}

.footer-column h3 {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 15px;
  color: #fff;
}

.footer-column ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-column li {
  margin-bottom: 10px;
}

.footer-column a {
  color: #aaa;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.3s;
  line-height: 1.6;
}

.footer-column a:hover {
  color: #fff;
}

.footer-contact {
  width: 320px;
  flex: 0 0 320px;
  margin-bottom: 30px;
}

.contact-info h3 {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 15px;
  color: #fff;
}

.contact-info p {
  margin: 8px 0;
  font-size: 14px;
  color: #aaa;
  line-height: 1.6;
}

.contact-info .phone {
  font-size: 26px;
  font-weight: bold;
  color: #fff;
  font-family: 'xygzjsb', 'SourceHanSerifCN Regular', Arial, sans-serif;
}

.footer-bottom {
  border-top: 1px solid #333;
  padding-top: 20px;
  width: 100%;
}

.footer-bottom .container {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding: 0;
}

.footer-bottom p {
  font-size: 14px;
  display: flex;
  flex-wrap: wrap;
  margin: 5px 0;
  color: rgba(255, 255, 255, 0.3);
  gap: 10px;
  line-height: 1.8;
}

.footer-bottom p a {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.3);
  text-decoration: none;
}

.footer-bottom p a:hover {
  color: rgba(255, 255, 255, 1);
}

@media (max-width: 1400px) {
  .footer-links {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  .footer-contact {
    width: 280px;
    flex-basis: 280px;
  }
}

@media (max-width: 1200px) {
  .footer-con {
    flex-direction: column;
  }

  .footer-links {
    width: 100%;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .footer-contact {
    width: 100%;
    flex-basis: auto;
  }
}

@media (max-width: 900px) {
  .footer-links {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .footer-bottom .container {
    flex-direction: column;
  }
}

@media (max-width: 600px) {
  .footer-links {
    grid-template-columns: 1fr;
  }
}
</style>
